import React, { useEffect, useRef, useState } from 'react';
import ShareSocial from '../ShareSocial';
import SubscriberLink from '../forms/SubscriberLink';

interface FlipCardData {
    id: string;
    frontImage: string;
    frontNumber: string;
    frontAlt: string;
    frontTitle: string;
    frontTagline: string;
    frontDescription: string;
    backTitle: string;
    backBoldText: string;
    backRegularText: string;
    backImage: string;
}

interface FdiTrendsData {
    tagline: string;
    heading: string;
    description: string;
    flipCards: FlipCardData[];
    pdfLink: string;
    downloadText: string;
}

interface FdiTrendsProps {
    data: FdiTrendsData;
}

const FDITrends: React.FC<FdiTrendsProps> = ({ data }) => {
    const [flippedCards, setFlippedCards] = useState<{ [key: string]: boolean }>({});
    const [currentSlide, setCurrentSlide] = useState(0);
    const totalSlides = data.flipCards.length;
    const [slideWidth, setSlideWidth] = useState(0);
    const slideRef = useRef<HTMLDivElement | null>(null);
    const touchStartX = useRef(0);
    const isDragging = useRef(false);
    const [offsetX, setOffsetX] = useState(0);

    const handleFlip = (id: string) => {
        setFlippedCards((prevState) => ({
            ...prevState,
            [id]: !prevState[id],
        }));
    };

    const handleNextSlide = () => {
        if (currentSlide < totalSlides - 1) {
            setCurrentSlide((prevSlide) => prevSlide + 1);
            setOffsetX(0);
        }
    };

    const handlePrevSlide = () => {
        if (currentSlide > 0) {
            setCurrentSlide((prevSlide) => prevSlide - 1);
            setOffsetX(0);
        }
    };

    const updateSlideWidth = () => {
        if (slideRef.current) {
            const width = slideRef.current.clientWidth;
            setSlideWidth(width);
        }
    };

    const handleTouchStart = (e: React.TouchEvent) => {
        const touch = e.touches[0];
        if (touch) {
            touchStartX.current = touch.clientX;
            isDragging.current = true;
        }
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (!isDragging.current) return;
        const touch = e.touches[0];
        if (touch) {
            const touchMoveX = touch.clientX;
            const deltaX = touchMoveX - touchStartX.current;

            // İlk slayttayken sola, son slayttayken sağa fazla kaymayı sınırlıyoruz
            if ((currentSlide === 0 && deltaX > 0) || (currentSlide === totalSlides - 1 && deltaX < 0)) {
                setOffsetX(deltaX / 4); // Sınırlı kaydırma efekti için direnç ekliyoruz
            } else {
                setOffsetX(deltaX);
            }
        }
    };

    const handleTouchEnd = () => {
        isDragging.current = false;

        if (offsetX < -slideWidth / 4 && currentSlide < totalSlides - 1) {
            handleNextSlide();
        } else if (offsetX > slideWidth / 4 && currentSlide > 0) {
            handlePrevSlide();
        } else {
            setOffsetX(0); // Yetersiz kaydırma yapıldığında geri ortalar
        }
    };

    useEffect(() => {
        updateSlideWidth();
        window.addEventListener('resize', updateSlideWidth);

        return () => {
            window.removeEventListener('resize', updateSlideWidth);
        };
    }, []);

    return (
        <section id="key-trends" className="key-trends bg-brown">
            <div className='section-regular'>
                <div className="container-large">
                    <div className="heading-wrap">
                        <div className="limited-heading-set">
                            <div className="tagline-heading">{data.tagline}</div>
                            <div className="heading-set-3">
                                <h2 className="h3-heading">{data.heading}</h2>
                                <div className="description">{data.description}</div>
                            </div>
                        </div>
                        <div className="action-side">
                            <SubscriberLink href={data.pdfLink} target="_blank" className="button-left-icon w-inline-block">
                                <div className="button-icon-left"></div>
                                <div className="text-block-4">{data.downloadText}</div>
                            </SubscriberLink>
                            <ShareSocial />
                        </div>
                    </div>
                </div>
            </div>
            <div
                data-delay="4000"
                data-animation="slide"
                className="testimonial-slider w-slider"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                <div className="team-slider-mask w-slider-mask">
                    {data.flipCards.map((card, index) => {
                        const isFlipped = flippedCards[card.id] || false; // Kartın durumu
                        const isActive = index === currentSlide;
                        return (
                            <div key={card.id}
                                className={`testimonial-slide slide w-slide ${isActive ? 'active' : ''}`}
                                style={{
                                    transform: `translateX(${-currentSlide * slideWidth + offsetX}px)`,
                                    transition: isDragging.current ? 'none' : 'transform 500ms ease',
                                    opacity: isActive ? 1 : 1,
                                }}
                                ref={index === 0 ? slideRef : null}
                            >
                                <div id={card.id} className={`flipcard-wrapper ${isFlipped ? 'flipped' : ''}`}>
                                    <div className="flipcard-front" >
                                        <img
                                            src={card.frontImage}
                                            alt={card.frontAlt}
                                            sizes="(max-width: 479px) 100vw, 460.796875px"
                                            className="flipping-card-image"
                                        />
                                        <div className="tagline-heading-2 turqoise">{card.frontTagline}</div>
                                        <h3 className="flipcard-title">
                                            <strong>{card.frontTitle}</strong>
                                        </h3>
                                        <div className="description white mini">{card.frontDescription}</div>
                                        <div className="flipcard-more" onClick={() => handleFlip(card.id)} >
                                            <span className="fa-solid-span"></span>
                                        </div>
                                    </div>
                                    <div className="flipcard-fdi-target-back">
                                        <h2 className="h4-heading">
                                            <strong>{card.backTitle}</strong>
                                        </h2>
                                        <div className="description mini">{card.backBoldText}</div>
                                        <div className="flipcard-less" onClick={() => handleFlip(card.id)}>
                                            <span className="fa-solid-span dark"></span>
                                        </div>
                                        <img
                                            src={card.backImage}
                                            alt={card.frontAlt}
                                            sizes="(max-width: 479px) 100vw, 460.796875px"
                                            className="flipping-card-image back"
                                        />
                                    </div>
                                </div>
                            </div>
                        )
                    }
                    )}
                </div>
                <div
                    className="generic-slider-trigger left cream w-slider-arrow-left"
                    onClick={handlePrevSlide}
                    aria-hidden={currentSlide === 0 ? "true" : "false"}
                >
                    <div className="slider-chevron"></div>
                </div>
                <div
                    className="generic-slider-trigger right cream w-slider-arrow-right"
                    onClick={handleNextSlide}
                    aria-hidden={currentSlide === totalSlides - 1 ? "true" : "false"}
                >
                    <div className="slider-chevron"></div>
                </div>
                <div className="slide-nav-hidden w-slider-nav w-round w-num"></div>
            </div>
        </section>
    );
};

export default FDITrends;
